import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.css'
import { Input } from '../../../components/ModalInputs'
import { StudentsContext } from '../../../utils/context'
import Button from '../../../components/Button'
import Confirmation from '../../../components/Confirmation/Confirmation'
import {
  getStudentFiles,
  uploadStudentFile,
  downloadStudentFile,
  deleteStudentFile,
  renameStudentFile,
} from '../../../actions/students/files'


export default function Documents() {
  const dispatch = useDispatch()
  const { allForms, addMode, studentTabData, updateInitialValues, studentId } = useContext(StudentsContext)

  const { values, setFieldValue, setValues } = allForms
  const { documents } = studentTabData || {}

  const { studentFiles } = useSelector(state => state.students.data)

  const lastSyncedRef = useRef(null)
  const fileInputRef = useRef(null)
  const downloadLinkRef = useRef(null)

  const [selectedFile, setSelectedFile] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [renameMode, setRenameMode] = useState(false)
  const [newFileName, setNewFileName] = useState('')
  const [jpegQuality, setJpegQuality] = useState(60)

  const handleDocumentsChange = (e) => {
    const { name, value } = e.target
    setFieldValue(`documents.${name}`, value)
  }

  useEffect(() => {
    const dataJson = JSON.stringify(documents)
    if (!addMode && documents && lastSyncedRef.current !== dataJson) {
      lastSyncedRef.current = dataJson
      setValues(prev => ({
        ...prev,
        documents: { ...documents }
      }))
      if (updateInitialValues) {
        updateInitialValues('documents', documents)
      }
    }
  }, [documents, addMode, setValues, updateInitialValues])

  useEffect(() => {
    if (!addMode && studentId) {
      dispatch(getStudentFiles(studentId))
    }
  }, [studentId, addMode]) // eslint-disable-line

  const handleUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    dispatch(uploadStudentFile(studentId, fd))
    e.target.value = ''
  }

  const handleDownload = async () => {
    if (selectedFile === null) return
    const fileName = studentFiles[selectedFile]?.file_name || studentFiles[selectedFile]?.name || studentFiles[selectedFile]
    const blob = await dispatch(downloadStudentFile(studentId, fileName))
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = downloadLinkRef.current
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const handlePreview = async () => {
    if (selectedFile === null) return
    const fileName = studentFiles[selectedFile]?.file_name || studentFiles[selectedFile]?.name || studentFiles[selectedFile]
    const blob = await dispatch(downloadStudentFile(studentId, fileName))
    if (!blob) return
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  const handleDeleteConfirm = () => {
    const fileName = studentFiles[selectedFile]?.file_name || studentFiles[selectedFile]?.name || studentFiles[selectedFile]
    dispatch(deleteStudentFile(studentId, fileName))
    setSelectedFile(null)
    setShowDeleteConfirm(false)
  }

  const handleRenameClick = () => {
    const fileName = studentFiles[selectedFile]?.file_name || studentFiles[selectedFile]?.name || studentFiles[selectedFile]
    setNewFileName(fileName)
    setRenameMode(true)
  }

  const handleRenameSave = () => {
    if (!newFileName.trim()) return
    const oldName = studentFiles[selectedFile]?.file_name || studentFiles[selectedFile]?.name || studentFiles[selectedFile]
    dispatch(renameStudentFile(studentId, oldName, newFileName.trim()))
    setRenameMode(false)
    setSelectedFile(null)
  }

  return (
    <div className={styles.tabWrapper}>
      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Свидетельство о рождении</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Серия'
            name='documentseria'
            value={(values.documents || {}).documentseria || ''}
            onChange={handleDocumentsChange}
            highlight
          />
          <Input
            label='Номер'
            name='documentnumber'
            value={(values.documents || {}).documentnumber || ''}
            onChange={handleDocumentsChange}
            highlight
          />
          <Input
            label='Когда выдан'
            type='date'
            name='documentwhengive'
            value={(values.documents || {}).documentwhengive || ''}
            onChange={handleDocumentsChange}
            highlight
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Кем выдан'
            name='documentwhogive'
            value={(values.documents || {}).documentwhogive || ''}
            onChange={handleDocumentsChange}
            style={{ width: '100%' }}
            highlight
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Паспорт</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Серия'
            name='documentseria2'
            value={(values.documents || {}).documentseria2 || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Номер'
            name='documentnumber2'
            value={(values.documents || {}).documentnumber2 || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Когда выдан'
            type='date'
            name='documentwhengive2'
            value={(values.documents || {}).documentwhengive2 || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Код подразделения'
            name='documentissuercode'
            value={(values.documents || {}).documentissuercode || ''}
            onChange={handleDocumentsChange}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Кем выдан'
            name='documentwhogive2'
            value={(values.documents || {}).documentwhogive2 || ''}
            onChange={handleDocumentsChange}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Загранпаспорт</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Серия'
            name='passportseria'
            value={(values.documents || {}).passportseria || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Номер'
            name='passportnumber'
            value={(values.documents || {}).passportnumber || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Действует до'
            type='date'
            name='passportdate'
            value={(values.documents || {}).passportdate || ''}
            onChange={handleDocumentsChange}
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Страховой полис</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Серия'
            name='insuranceseria'
            value={(values.documents || {}).insuranceseria || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Номер'
            name='insurancenumber'
            value={(values.documents || {}).insurancenumber || ''}
            onChange={handleDocumentsChange}
          />
          <Input
            label='Действует до'
            type='date'
            name='insurancedate'
            value={(values.documents || {}).insurancedate || ''}
            onChange={handleDocumentsChange}
          />
        </div>
        <div className={styles.inputsRow}>
          <Input
            label='Страховая компания'
            name='insurancecompany'
            value={(values.documents || {}).insurancecompany || ''}
            onChange={handleDocumentsChange}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <h3 className={styles.sectionTitle}>Прочее</h3>
        <div className={styles.inputsRow}>
          <Input
            label='СНИЛС'
            name='pensionss'
            value={(values.documents || {}).pensionss || ''}
            onChange={handleDocumentsChange}
            style={{ width: '300px' }}
          />
          <Input
            label='ИНН'
            name='inn'
            value={(values.documents || {}).inn || ''}
            onChange={handleDocumentsChange}
            style={{ width: '300px' }}
          />
        </div>
      </div>

      <div className={styles.qualificationForm}>
        <input ref={fileInputRef} type='file' hidden onChange={handleUpload} />
        <a ref={downloadLinkRef} hidden />

        <div className={styles.filePanel}>
          <div className={styles.filesList}>
            {(studentFiles || []).map((file, idx) => {
              const name = file?.file_name || file?.name || file
              return (
                <div
                  key={name || idx}
                  onClick={() => { setSelectedFile(idx); setRenameMode(false) }}
                  className={`${styles.fileItem} ${selectedFile === idx ? styles.fileItemSelected : ''}`}
                >
                  {name}
                </div>
              )
            })}
          </div>
          <div className={styles.filesButtons}>
            <Button mode='bordered' onClick={() => fileInputRef.current.click()}>+ Добавить</Button>
            <Button mode='bordered' onClick={handleDownload} disabled={selectedFile === null}>Получить</Button>
            <Button mode='bordered' onClick={handlePreview} disabled={selectedFile === null}>Просмотр</Button>
            <Button mode='bordered' onClick={() => setShowDeleteConfirm(true)} disabled={selectedFile === null}>Удалить</Button>
            <Button mode='bordered' onClick={handleRenameClick} disabled={selectedFile === null}>Переименовать</Button>
          </div>
        </div>

        {renameMode && (
          <div className={styles.renameRow}>
            <Input
              label='Новое имя'
              value={newFileName}
              onChange={e => setNewFileName(e.target.value)}
              style={{ width: '300px' }}
            />
            <Button mode='bordered' onClick={handleRenameSave}>Сохранить</Button>
            <Button mode='bordered' onClick={() => setRenameMode(false)}>Отмена</Button>
          </div>
        )}

        <div className={styles.fileBottomRow}>
          <Input
            label='Качество JPEG'
            type='number'
            value={jpegQuality}
            onChange={e => setJpegQuality(e.target.value)}
            style={{ width: '80px' }}
          />
          <Button mode='bordered' disabled>+ Сканировать</Button>
        </div>
      </div>

      {showDeleteConfirm && (
        <Confirmation
          title='Вы уверены, что хотите удалить файл?'
          acceptConfirmation={handleDeleteConfirm}
          rejectConfirmation={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  )
}
